import React, { useState, useMemo, useCallback, useEffect } from 'react'
import {
  Button, Stack, Typography, FormControl, Select, MenuItem
} from '@mui/material'
import { useNavigate } from 'react-router-dom'
import { useIsMobile } from '@/hooks/Responsive'
import BoardList from '@components/Board/BoardList'
import BoardListTable from '@components/Board/BoardListTable'
import { fetchMngQnaList, fetchMyManagementComboList } from '@/api/mypage'
import { useLang } from '@/i18n/useLang'
import { buildLangHref } from '@data/nav'
import { useAuth } from '@/components/AuthContext'

export function createQnaTableColumns({ isMobile, lang }) {
  const renderTitle = (v, row) => (
    <Typography
      component="a"
      href={buildLangHref(`/mypage/qna/${row.bulletinId}`, lang)}
      variant={isMobile ? 'subtitle3' : 'body1'}
      fontWeight={isMobile ? '600' : '400' }
      sx={{
        cursor: 'pointer',
        overflow: isMobile ? 'visible' : 'hidden',
        textOverflow: isMobile ? 'unset' : 'ellipsis',
        whiteSpace: isMobile ? 'normal' : 'nowrap',
        wordBreak: 'keep-all',
        '&:hover': { textDecoration: 'underline', fontWeight: '700' },
      }}
    >
      {v}
    </Typography>
  )

  const renderLecture = (v) => (
    <Typography 
      sx={{
        overflow: isMobile ? 'visible' : 'hidden',
        textOverflow: isMobile ? 'unset' : 'ellipsis',
        whiteSpace: isMobile ? 'normal' : 'nowrap',
        wordBreak: 'keep-all',
      }}
    >
      {v?.trim() || '-'}
    </Typography>
  )

  const renderStatus = (v) => (
    <Typography
      sx={{
        color: v === '대기' ? 'secondary.main' : 'text.primary',
      }}
    >
      {v}
    </Typography>
  )

  return [
    { field: 'rowNo', role: 'no', headerName: '번호', sortable: false, width: '70px', align: 'center', hideTitleInCard: true },
    // { field: 'qDepartment', headerName: '학과', sortable: false, width: '120px', align: 'center' },
    { field: 'eduLectureName', role: 'lecture', headerName: '강좌명', sortable: false, width: '220px', align: 'left', render: renderLecture },
    { field: 'bulletinTitle', role: 'title', headerName: '제목', sortable: false, align: 'left', render: renderTitle },
    { field: 'replyStatus', role: 'status', headerName: '답변유무', sortable: false, width: '90px', align: 'center', render: renderStatus },
    { field: 'registDttm', role: 'date', headerName: '등록일', sortable: false, width: '120px', align: 'center' },
    { field: 'memberName', role: 'author', headerName: '등록자', sortable: false, width: '120px', align: 'center' },
  ]
}

/* 상단 검색 */
export function QnaToolbar({ isMobile, status, department, onStatusChange, onDepartmentChange, departments = [] }) {
  return (
    <Stack
      direction={isMobile ? 'column' : 'row'}
      alignItems="center"
      spacing={1}
      sx={{
        flex: 'auto',
        width: isMobile ? '100%' : 'auto'
      }}
    >
      {/* 상태 필터 */}
      <FormControl sx={{ flex: 'auto', width: isMobile ? '100%' : '220px', }}>
        <Select
          name="status"
          value={status}
          onChange={(e) => onStatusChange?.(e.target.value)}
          displayEmpty
          MenuProps={{
            PaperProps: {
              sx: {
                width: '100%',
                maxWidth: (theme) => isMobile ? `calc(100vw - ${theme.spacing(8)})` : '220px',
              },
            },
          }}
          sx={{ 
            width: '100%',
            maxWidth: (theme) => isMobile ? `calc(100vw - ${theme.spacing(8)})` : '220px',
          }}
        >
          <MenuItem value="전체">전체</MenuItem>
          <MenuItem value="Y">완료</MenuItem>
          <MenuItem value="N">대기</MenuItem>
        </Select>
      </FormControl>

      
      {/* 학과 */}
      <FormControl sx={{ flex: 'auto', width: isMobile ? '100%' : '200px' }}>
        <Select
          name="department"
          value={department}
          onChange={(e) => onDepartmentChange(e.target.value)}
          displayEmpty
          MenuProps={{
            PaperProps: {
              sx: {
                width: '100%',
                maxWidth: (theme) =>
                  isMobile ? `calc(100vw - ${theme.spacing(8)})` : '200px',
              },
            },
          }}
          sx={{
            width: '100%',
            maxWidth: (theme) =>
              isMobile ? `calc(100vw - ${theme.spacing(8)})` : '200px',
          }}
        >
          <MenuItem value="전체">전체</MenuItem>
          {departments.map((dep) => (
            <MenuItem key={dep.code} value={dep.code}>
              {dep.codeNm}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </Stack>
  )
}

export default function MyQna() {
  const navigate = useNavigate()
  const { isMobile } = useIsMobile()
  const lang = useLang()

  const [departments, setDepartments] = useState([]);
  const [statusFilter, setStatusFilter] = useState('전체') 
  const [departmentFilter, setDepartmentFilter] = useState('전체')
  const [confirmedStatus, setConfirmedStatus] = useState('전체') 
  const [confirmedDepartment, setConfirmedDepartment] = useState('전체') 
  const [confirmedSearch, setConfirmedSearch] = useState('')

  const { user, loading, isAuthReady } = useAuth();

  useEffect(() => {
    const getDepartments = async () => {
      try {
        const response = await fetchMyManagementComboList({ params: { lecturerAccountId: user.accountId } });
        setDepartments(response.data || []);
      } catch (error) {
        console.error("Failed to fetch departments:", error);
      }
    };
    getDepartments();
  }, []);

  const handleStatusChange = useCallback((value) => {
    setStatusFilter(value)
  }, [])

  const handleDepartmentChange = useCallback((value) => {
    setDepartmentFilter(value)
  }, [])

  const fetcher = useCallback(
    async ({ page, pageSize, sort }) => {
      const params = {
        page: page,
        size: pageSize,
        sort: sort ? `${sort.field},${sort.direction}` : 'rowNo,desc',
        replyYn: confirmedStatus !== '전체' ? confirmedStatus : undefined,
        eduLectureId: confirmedDepartment !== '전체' ? confirmedDepartment : undefined,
        keyword: confirmedSearch || undefined,
        accountId: user.accountId
      };

      Object.keys(params).forEach(key => params[key] === undefined && delete params[key]);

      try {
        const response = await fetchMngQnaList({ params });
        const { content, totalElements, totalPages } = response.data;
        return { items: content, total: totalElements, totalPages };
      } catch (error) {
        console.error("Failed to fetch Q&A list:", error);
        return { items: [], total: 0, totalPages: 1 };
      }
    },
    [confirmedStatus, confirmedDepartment, confirmedSearch] 
  )

  const columns = useMemo(
    () => createQnaTableColumns({ isMobile, lang }),
    [isMobile, lang]
  )

  return (
    <BoardList
      view="table"
      columns={columns}
      fetcher={fetcher}
      TableComponent={BoardListTable}
      initialSort={{ field: 'rowNo', direction: 'desc' }}
      initialPage={1}
      initialPageSize={10}
      toolbarLeft={
        <QnaToolbar
          isMobile={isMobile}
          status={statusFilter}
          department={departmentFilter} 
          onStatusChange={handleStatusChange}
          onDepartmentChange={handleDepartmentChange}
          departments={departments}
        />
      }
      onSearchSubmit={(searchValue) => {
        setConfirmedStatus(statusFilter)
        setConfirmedDepartment(departmentFilter)
        setConfirmedSearch(searchValue)
      }}
      emptyMessage="등록된 Q&A 내역이 없습니다." 
    >
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="flex-end"
        sx={{ mt: isMobile ? 3 : 5 }}
      >
      </Stack>
    </BoardList>
  )
}